import { injectCompanyToURL } from '@helpers'

import type { CompanyConsentTypes } from '@contexts/CompanyConsentProvider'

import Api from '../api'
import type { MonthlyTaxCreateFormValues, MonthlyTaxUpdateFormValues } from './types'
import {
  API_ASSIGNMENT_NOTE_RECOMMENDATIONS_URL,
  API_BULK_TAGGING_INIT_URL,
  API_BULK_TAGGING_UPDATE_URL,
  API_COMPANY_CONSENTS_URL,
  API_CONFIG_URL,
  API_DASHBOARD_CHECK_URL,
  API_EVENTS_LOG_URL,
  API_LEDGER_NUMBERS_URL,
  API_OAUTH_AUTHORIZE_URL,
  API_TAGS_URL,
  API_TAX_CODE_LIST_URL,
  API_TAX_GROUP_DETAIL_URL,
  API_TAX_GROUP_LIST_URL,
  API_TAX_LIST_URL,
  API_TAX_USER_VIEW_URL,
} from './urls'

export function initialize() {
  return Api.get(API_CONFIG_URL)
}

export function dashboardStatusCheck(companyId: Company['id']) {
  return Api.get(injectCompanyToURL(API_DASHBOARD_CHECK_URL, companyId))
}

// INVOICE TAGS
export function fetchTags(companyId: Company['id']) {
  return Api.get(injectCompanyToURL(API_TAGS_URL, companyId))
}

export function createTag(
  companyId: Company['id'],
  data: {
    name: string
    company: number
  }
) {
  return Api.post(injectCompanyToURL(API_TAGS_URL, companyId), data)
}

// VAT
export function fetchTaxGroupDetails(companyId: Company['id'], monthlyTaxId: number) {
  return Api.get(injectCompanyToURL(API_TAX_LIST_URL, companyId).replace('{monthly_tax_id}', String(monthlyTaxId)))
}

export function userViewTax(companyId: Company['id'], monthlyTaxId: ItemIdType) {
  return Api.post(
    injectCompanyToURL(API_TAX_USER_VIEW_URL, companyId).replace('{monthly_tax_id}', String(monthlyTaxId))
  )
}

export function createMonthlyTax(companyId: Company['id'], data: MonthlyTaxCreateFormValues) {
  return Api.post(injectCompanyToURL(API_TAX_GROUP_LIST_URL, companyId), data)
}

export function updateMonthlyTax(companyId: Company['id'], data: MonthlyTaxUpdateFormValues) {
  return Api.patch(
    injectCompanyToURL(API_TAX_GROUP_DETAIL_URL, companyId).replace('{monthly_tax_id}', String(data.id)),
    data
  )
}

export function removeMonthlyTax(companyId: Company['id'], monthlyTaxId: ItemIdType) {
  return Api.delete(
    injectCompanyToURL(API_TAX_GROUP_DETAIL_URL, companyId).replace('{monthly_tax_id}', String(monthlyTaxId))
  )
}

// TAX CODES
export function fetchTaxCodes(companyId: Company['id']) {
  return Api.get(injectCompanyToURL(API_TAX_CODE_LIST_URL, companyId))
}

// BULK TAGGING
export function initTagging(companyId: Company['id'], data: TaggingInitRequestPayload) {
  return Api.post(injectCompanyToURL(API_BULK_TAGGING_INIT_URL, companyId), data)
}

export function bulkTagging(companyId: Company['id'], data: BulkTaggingPayload) {
  return Api.post(injectCompanyToURL(API_BULK_TAGGING_UPDATE_URL, companyId), data)
}

//* ACCOUNTING (Yeri)
export function fetchAssignmentNoteRecommendations(companyId: Company['id']) {
  return Api.get(injectCompanyToURL(API_ASSIGNMENT_NOTE_RECOMMENDATIONS_URL, companyId))
}

export function fetchLedgerNumbers(companyId: Company['id']) {
  return Api.get(injectCompanyToURL(API_LEDGER_NUMBERS_URL, companyId))
}

export function createLedgerNumber(companyId: Company['id'], data: { code: string; name: string }) {
  return Api.post(injectCompanyToURL(API_LEDGER_NUMBERS_URL, companyId), data)
}

export function fetchEventsLogData(
  companyId: Company['id'],
  { documentId, documentType }: { documentId: number; documentType: 'document' | 'expense' | 'income' }
) {
  return Api.get(
    injectCompanyToURL(API_EVENTS_LOG_URL, companyId)
      .replace('{document_type}', documentType)
      .replace('{document_id}', String(documentId))
  )
}

//* Company consents
export function getCompanyConsents(companyId: Company['id']) {
  return Api.get(injectCompanyToURL(API_COMPANY_CONSENTS_URL, companyId))
}

export function setCompanyConsent(companyId: Company['id'], consentType: CompanyConsentTypes) {
  return Api.post(injectCompanyToURL(API_COMPANY_CONSENTS_URL, companyId), { consent_type: consentType })
}

//* OAuth
export function initOAuth(params: OAuthParams) {
  return Api.get(API_OAUTH_AUTHORIZE_URL, { params })
}
